import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later, next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { SourceTypeCategory } from 'webapp/enums/enums';
import { TourId } from 'webapp/enums/tour';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive, PopularLazyLoadSelector, sortBy } from 'webapp/utils/common';

import makeArray from 'webapp/helpers/make-array';
import visibleCheckContainer from 'webapp/modifiers/visible-check-container';
import CommonListLazyLoadComponent from 'webapp/components/common/list/lazy-load';
import CommonListLoaderComponent from 'webapp/components/common/list/loader';
import DataManagerGridItemPlaceholderRequestSourceTypeComponent from 'webapp/components/data-manager/grid-item-placeholder-request-source-type';
import DataManagerNoResultsComponent from 'webapp/components/data-manager/no-results';
import SubmitButtonComponent from 'webapp/components/submit-button';
import WizardSearchBrowseBannerComponent from 'webapp/components/wizard/search-browse-banner';
import WizardSourcesGridItemComponent from 'webapp/components/wizard/sources/grid-item';

import BatchSelect from 'webapp/models-custom/batch-select';
import FilterSortAvailableSourceInWizard from 'webapp/models-custom/filter-sort/available-source-in-wizard';
import type { FilterIdType } from 'webapp/models-custom/filter-sort/base';
import type SourceType from 'webapp/models-custom/source-type';
import type OnboardingSourceTour from 'webapp/models-custom/tour/onboarding-source';

type Args = Record<string, never>;

export default class WizardSourceComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('flow/tour/onboarding')
	declare flowTourOnboardingService: Service.Flow.Tour.Onboarding;
	@service('tour') declare tourService: Service.Tour;
	@service('wizard') declare wizardService: Service.Wizard;
	@service('onboarding') declare onboardingService: Service.Onboarding;
	@service('analytics') declare analyticsService: Service.Analytics;

	filterSort: FilterSortAvailableSourceInWizard;
	tour: OnboardingSourceTour | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const filterSort = new FilterSortAvailableSourceInWizard({
			selectors: PopularLazyLoadSelector.grid,
			getAllItems: () => {
				return this.sortedAvailableSources;
			},
		});

		this.filterSort = filterSort;

		if (!this.wizardService.sourcesBatchSelect) {
			next(this, this.initializeBatchSelect);
		}
	}

	get batchSelect() {
		return this.wizardService.sourcesBatchSelect;
	}

	get selectedSourcesCount(): number {
		return this.batchSelect?.selectedItems.length || 0;
	}

	get selectedSourceType() {
		return this.wizardService.selectedSourceType;
	}

	get isNextButtonDisabled(): boolean {
		return this.selectedSourcesCount === 0;
	}

	get showRequestSourceOption() {
		return !this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins;
	}

	get sourceValues() {
		return this.filterSort.categoryOptions.map((item) => {
			return {
				value: item.id,
				label: item.text === 'All' ? 'All categories' : item.text,
			};
		});
	}

	@cached
	get firstFilterInFilterSort() {
		return this.filterSort.findFilter(1);
	}

	get filter1Selected(): string | null {
		return (this.firstFilterInFilterSort?.selected[0] as string) || null;
	}

	connectedSourcesFilterFn = (sources: SourceType[]) => {
		return sources
			.filter((sourceType) => {
				return (
					sourceType.isAlreadyConnected &&
					(this.flowTourOnboardingService.isBannerShown
						? sourceType.category === SourceTypeCategory.CLOUD
						: !sourceType.isWithCustomToken)
				);
			})
			.filter((item) => !item.isGroup);
	};

	availableSourcesFilterFn = (sources: SourceType[]) => {
		return sources
			.filter((source) => {
				return !source.isAlreadyConnected || source.isWithCustomToken;
			})
			.filter((item) => !item.showInGroupOnly);
	};

	@cached
	get showSourceWrapperTitle() {
		if (this.filter1Selected !== 'all' || this.filterSort.typedFilter) {
			return !this.noResults;
		} else {
			const connectedSources = this.connectedSourcesFilterFn(this.sortedAvailableSources);
			const availableSources = this.availableSourcesFilterFn(this.sortedAvailableSources);
			return connectedSources.length > 0 && availableSources.length > 0;
		}
	}

	get noResults() {
		return this.filterSort.noResults;
	}

	get availableSourcesFromWizard() {
		return this.wizardService.availableSources;
	}

	get sortedAvailableSources() {
		const recommendedIntegrations = this.availableSourcesFromWizard.filter(
				(item) => item.isRecommended,
			),
			otherIntegrations = this.availableSourcesFromWizard.filter(
				(item) => !item.isRecommended,
			);

		return [
			...sortBy(recommendedIntegrations, 'popularity'),
			...sortBy(otherIntegrations, 'popularity'),
		];
	}

	trackAndGoNext(sourceType: SourceType) {
		this.analyticsService.event(TrackingEvent.ONBOARDING_SOURCE_CHOSEN, {
			datasource: sourceType.name,
			datasources: [sourceType.key],
			type: this.onboardingService.getType([sourceType]),
			origin: 'wizard',
		});

		void this.wizardService.goToNext();
	}

	private initializeBatchSelect() {
		const sourcesBatchSelect = new BatchSelect<SourceType>({
			getAllItems: () => {
				return [];
			},
			getOptions: () => {
				return [];
			},
			doBatchAction: () => {
				return Promise.resolve();
			},
			onItemAdded: () => {
				this.wizardService.adjustVisitedSteps();
			},
			maxSelectedItems: 1, // for now we limit to single selection, in the future it will be multiple. ASANA TASK: https://app.asana.com/0/1201898938264596/1207442546186058/f
			replaceSelectionOnMaxItemsReached: true,
		});

		this.wizardService.sourcesBatchSelect = sourcesBatchSelect;
	}

	@action
	startOrContinueTour(source: SourceType) {
		if (this.flowTourOnboardingService.isBannerShown) {
			this.flowTourOnboardingService.continueTour();
		} else {
			later(() => {
				if (
					isAlive(this) &&
					this.selectedSourceType === source &&
					this.tourService.isEligibleForOnboardingSourceTour
				) {
					const tour = this.tourService.initTour(TourId.ONBOARDING_SOURCE);
					this.tour = tour;
					tour?.start();
				}
			}, 5000);
		}
	}

	@action
	selectCategory(category: FilterIdType) {
		const filter1 = this.firstFilterInFilterSort;
		if (filter1) {
			filter1.selectOption(category);
		}
	}

	@action
	next() {
		const sourceType = this.selectedSourceType;

		if (this.tour) {
			this.tour.end();
			this.tour = null;
		}

		if (sourceType) {
			if (sourceType.isGroup) {
				this.mmSourceTypesService.openSourceTypeInfoPopup(sourceType, (sourceType) => {
					// when we select Excel, we need to clear the selection and add the selected source type from popup
					this.batchSelect?.unselectAll();
					this.batchSelect?.addItem(sourceType);
					this.trackAndGoNext(sourceType);
				});
			} else {
				this.trackAndGoNext(sourceType);
			}
		}
	}

	@action
	sourceRequest() {
		this.wizardService.requestSource();
	}

	@action
	autoSelectSource(source: SourceType) {
		this.batchSelect?.addItem(source);

		next(() => {
			this.next();
		});
	}

	@action
	changeSearch(value: string) {
		const filterSort = this.filterSort;
		filterSort.typedFilter = value;
	}

	@action
	resetFilter() {
		this.filterSort.resetFilters();
	}
	<template>
		<WizardSearchBrowseBannerComponent
			@searchTypedFilter={{this.filterSort.typedFilter}}
			@changeSearch={{this.changeSearch}}
			@browseName="category"
			@browseSelected={{this.filter1Selected}}
			@browseOptions={{this.sourceValues}}
			@browseBy="category"
			@onBrowseChange={{this.selectCategory}}
		/>

		<div class="content">
			<div class="list">
				{{#if this.noResults}}
					<DataManagerNoResultsComponent
						@model={{this.sortedAvailableSources}}
						@filterSort={{this.filterSort}}
						@onOpenSourceRequest={{this.sourceRequest}}
						@onConnectOrCreateToken={{this.autoSelectSource}}
						@resetFilter={{this.resetFilter}}
						@showDemoButton={{true}}
					/>
				{{/if}}
				<div class="scroll">
					<div
						class="sources-wrapper sources-wrapper--connected
							{{unless this.showSourceWrapperTitle 'mb0'}}
							{{if this.filterSort.noResults 'hide'}}"
					>
						{{#if this.showSourceWrapperTitle}}
							<span class="label">Already connected sources</span>
						{{/if}}

						<CommonListLazyLoadComponent
							@classNames="lazy-grid"
							@filterSort={{this.filterSort}}
							@filterExtraFn={{this.connectedSourcesFilterFn}}
							@initialRowCount={{10}}
							@loadMoreRowCount={{5}}
							@animationProperty="id"
							@autoLoaded={{true}}
							@bottomDistance={{700}}
							as |visibleItems exporter|
						>
							<ul
								class="sources-ct flex-ct flex-ct--size-source flex-ct--size-source-condensed"
								{{visibleCheckContainer}}
							>
								{{#each visibleItems as |item|}}
									<WizardSourcesGridItemComponent
										@model={{item}}
										@batchSelect={{this.batchSelect}}
										@onClick={{this.startOrContinueTour}}
									/>
								{{/each}}
								{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
								{{#each (makeArray length=exporter.itemsPerRow)}}
									<li class="flex-item flex-item--fake"></li>
								{{/each}}
							</ul>
							{{#unless exporter.areAllItemsLoaded}}
								<CommonListLoaderComponent />
							{{/unless}}
						</CommonListLazyLoadComponent>
					</div>

					<div
						class="sources-wrapper sources-wrapper--available
							{{if this.filterSort.noResults 'hide'}}"
					>
						{{#if this.showSourceWrapperTitle}}
							<span class="label">All available sources</span>
						{{/if}}
						<CommonListLazyLoadComponent
							@classNames="lazy-grid"
							@filterSort={{this.filterSort}}
							@filterExtraFn={{this.availableSourcesFilterFn}}
							@initialRowCount={{10}}
							@loadMoreRowCount={{5}}
							@animationProperty="id"
							@autoLoaded={{true}}
							@bottomDistance={{700}}
							as |visibleItems exporter|
						>
							<ul
								class="sources-ct flex-ct flex-ct--size-source flex-ct--size-source-condensed"
								{{visibleCheckContainer}}
							>
								{{#each visibleItems as |item|}}
									<WizardSourcesGridItemComponent
										@model={{item}}
										@batchSelect={{this.batchSelect}}
										@onClick={{this.startOrContinueTour}}
									/>
								{{/each}}
								{{#if this.showRequestSourceOption}}
									<DataManagerGridItemPlaceholderRequestSourceTypeComponent
										@onSourceRequest={{this.sourceRequest}}
									/>
								{{/if}}
								{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
								{{#each (makeArray length=exporter.itemsPerRow)}}
									<li class="flex-item flex-item--fake"></li>
								{{/each}}
							</ul>
							{{#unless exporter.areAllItemsLoaded}}
								<CommonListLoaderComponent />
							{{/unless}}
						</CommonListLazyLoadComponent>
					</div>
				</div>
			</div>

			<div class="wizard-controls smart-center-pos">
				<div class="container">
					<SubmitButtonComponent
						@text="Continue"
						@extraClass="next"
						@size="big"
						@isDisabled={{this.isNextButtonDisabled}}
						@noDelay={{true}}
						@onTrigger={{this.next}}
					/>
				</div>
			</div>
		</div>
	</template>
}
